import React, { useState } from 'react';
import {
  Bell,
  BellRing,
  Bot,
  Briefcase,
  Calendar,
  Folder,
  IndianRupee,
  LayoutDashboard,
  MessageCircle,
  Newspaper,
  Settings,
  Home,
  Menu,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { NotificationProvider } from '../context/NotificationContext';
import { NotificationBadge } from '../components/NotificationBadge';
import { MobileDrawer } from '../ui';


const navItems = [
  { id: 'dashboard',     label: 'Dashboard',      icon: LayoutDashboard },
  { id: 'bookings',      label: 'Bookings',        icon: Folder          },
  { id: 'cases',         label: 'Cases',           icon: Briefcase       },
  { id: 'ai-help',       label: 'LEX.ai',          icon: Bot, badge: 'AI'},
  { id: 'chat',          label: 'Chat',            icon: MessageCircle   },
  { id: 'schedule',      label: 'Schedule',        icon: Calendar        },
  { id: 'payments',      label: 'Earnings',        icon: IndianRupee     },
  { id: 'demand-draft',  label: 'News',            icon: Newspaper       },
  { id: 'notifications', label: 'Notifications',   icon: BellRing        },
  { id: 'settings',      label: 'Settings',        icon: Settings        },
];

const labelMap = navItems.reduce((acc, item) => {
  acc[item.id] = item.label;
  return acc;
}, {});

export const MainLayout = ({
  children,
  currentPage,
  onNavigate,
  onLogout,
  socket,
  profileGuardActive = false,
  profileGuardMessage = "",
  profileGuardAllowedPage = null,
}) => {
  const { user } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const pageTitle =
    currentPage === 'dashboard'
      ? `Hello, ${user?.name ? user.name.replace('Adv. ', '') : 'Advocate'}`
      : currentPage === 'verification-pending'
        ? 'Verification Pending'
      : labelMap[currentPage] || 'Dashboard';

  return (
    <NotificationProvider socket={socket}>
      <div className="h-screen bg-[#f3f5f8] overflow-hidden">
        <MobileDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          navItems={navItems}
          currentPage={currentPage}
          onNavigate={onNavigate}
          onLogout={onLogout}
          user={user}
          profileGuardActive={profileGuardActive}
          profileGuardAllowedPage={profileGuardAllowedPage}
        />
        <div className="flex h-full">
          <aside 
            className="hidden lg:flex w-[226px] h-full overflow-y-auto text-white flex-col border-r border-white/10 shadow-[8px_0_24px_rgba(2,6,23,0.2)]"
            style={{
              backgroundColor: '#000000',
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
              backgroundSize: '28px 28px',
            }}
          >
            <div className="px-5 py-5 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-sm font-bold">FM</div>
                <div>
                  <p className="text-lg font-bold leading-none">FindMyLawyer</p>
                  <p className="text-xs text-slate-300 mt-1">Lawyer Portal</p>
                </div>
              </div>
            </div>

            <div 
              className="px-4 py-3 border-b border-white/10 cursor-pointer hover:bg-white/5 transition-all group"
              onClick={() => onNavigate('settings', { activeTab: 'profile' })}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-blue-500 flex items-center justify-center text-sm font-semibold group-hover:shadow-[0_0_12px_rgba(59,130,246,0.6)] transition-shadow overflow-hidden">
                  {user?.profilePhoto || user?.profile_photo_url ? (
                    <img src={user.profilePhoto || user.profile_photo_url} alt={user?.name || "Profile"} className="w-full h-full object-cover" />
                  ) : (
                    user?.name?.[0] || 'A'
                  )}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold truncate group-hover:text-blue-400 transition-colors">{user?.name || 'Razique Hasan'}</p>
                </div>
              </div>
            </div>

            <nav className="flex-1 px-3 py-4 space-y-1.5">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = currentPage === item.id;

                return (
                  <button
                    key={item.id}
                    id={`nav-${item.id}`}
                    onClick={() => onNavigate(item.id)}
                    disabled={
                      profileGuardActive &&
                      profileGuardAllowedPage != null &&
                      item.id !== profileGuardAllowedPage
                    }
                    className={`w-full text-left flex items-center justify-between px-3 py-2.5 rounded-xl transition-colors ${
                      isActive ? 'bg-slate-700/60 text-white border border-white/10' : 'text-slate-300 hover:bg-slate-700/35'
                    } ${
                      profileGuardActive &&
                      profileGuardAllowedPage != null &&
                      item.id !== profileGuardAllowedPage
                        ? 'opacity-50 cursor-not-allowed hover:bg-transparent'
                        : ''
                    }`}
                  >
                    <span className="flex items-center gap-3 text-sm font-medium">
                      <Icon size={16} />
                      {item.label}
                    </span>
                    {item.badge && (
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          item.badge === 'AI' ? 'bg-white/10 text-slate-300' : 'bg-blue-500 text-white'
                        }`}
                      >
                        {item.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>

            <div className="px-5 py-4 border-t border-white/10 text-xs text-slate-400">
              <p>Subscription: Active</p>
            </div>
          </aside>

          <div className="flex-1 min-w-0 min-h-0 flex flex-col">
            <header className="min-h-[66px] bg-white/95 backdrop-blur-md border-b border-slate-200 px-3 sm:px-6 lg:px-8 flex items-center justify-between sticky top-0 z-20 safe-top py-2">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 mr-2">
                <button
                  onClick={() => setDrawerOpen(true)}
                  className="lg:hidden flex items-center justify-center text-slate-700 hover:text-black rounded-xl border border-slate-200 bg-white shadow-xs shrink-0 transition-colors"
                  style={{ minWidth: '44px', minHeight: '44px' }}
                  aria-label="Open navigation menu"
                >
                  <Menu size={22} />
                </button>
                <h1 className="text-lg sm:text-2xl lg:text-3xl font-extrabold text-slate-900 truncate pt-0.5">{pageTitle}</h1>
              </div>

              <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                <button 
                  onClick={() => window.location.href = '/'}
                  className="w-10 h-10 rounded-xl border border-slate-200 text-slate-700 relative bg-white flex items-center justify-center hover:bg-slate-50 transition-colors shrink-0"
                  style={{ minWidth: '40px', minHeight: '40px' }}
                  title="Go to Home"
                >
                  <Home size={16} />
                </button>

                {/* Dynamic notification bell with badge + dropdown */}
                <NotificationBadge onNavigate={onNavigate} />

                {currentPage !== 'dashboard' && (
                  <button onClick={onLogout} className="hidden sm:flex px-4 py-2.5 rounded-xl bg-[#071b33] text-white text-sm font-semibold shrink-0">
                    Logout
                  </button>
                )}
              </div>
            </header>

            <main key={currentPage} className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-5">
              {profileGuardActive && profileGuardMessage ? (
                <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">
                  {profileGuardMessage}
                </div>
              ) : null}
              {children}
            </main>
          </div>
        </div>
      </div>
    </NotificationProvider>
  );
};
